import { Card } from '@chakra-ui/react';
import {
  PaymentMethodShareInitResponse,
  PaymentMethodSummaryResponse,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import type { Stripe, StripeElements } from '@stripe/stripe-js';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import {
  useCreatePaymentMethodFromShare,
  usePaymentShareInitData,
} from '../../../api/sharePaymentMethodService';
import AppLoading from '../../../components/App/AppLoading';
import {
  MAlert,
  MBox,
  MButton,
  MCard,
  MFlex,
  MImage,
  MText,
} from '../../../components/Monetize';
import { StripeWrapper } from '../../../components/PaymentMethods/Form/StripeWrapper';
import { PAPER_PAGE_MAX_WIDTH } from '../../../constants/quotes';
import { logger } from '../../../services/logger';
import Sentry from '../../../services/sentry';
import { PaymentMethodSharePaymentMethod } from './PaymentMethodSharePaymentMethod';

export const PaymentMethodSharePage = () => {
  const pathParams = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const { tenantId, recordId: accountId } = pathParams;
  const token = searchParams.get('token');
  const paymentGatewayId = searchParams.get('pgId');
  const billGroupId = searchParams.get('bgId') || undefined;

  const [stripeData, setStripeData] = useState<{
    stripe?: Maybe<Stripe>;
    elements?: Maybe<StripeElements>;
  }>();
  const [stripeState, setStripeState] = useState<'LOADING' | 'ERROR' | 'READY'>(
    'LOADING',
  );

  const [shareData, setShareData] = useState<PaymentMethodShareInitResponse>();
  const [isLoading, setIsLoading] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string>();
  const [savedPaymentMethod, setSavedPaymentMethod] =
    useState<PaymentMethodSummaryResponse>();

  const { handleSubmit } = useForm();

  const { mutateAsync: generateSetupIntent, isPending: _isLoadingSetupIntent } =
    usePaymentShareInitData({
      onSuccess(keys) {
        setShareData(keys);
      },
      onError: (error) => {
        const params = new URLSearchParams(searchParams);
        params.set(
          'error',
          'The link you are trying to access is no longer valid. Contact us for a new link.',
        );
        setSearchParams(params);
      },
    });

  const { mutateAsync: savePaymentMethod } = useCreatePaymentMethodFromShare();

  useEffect(() => {
    if (paymentGatewayId && accountId && token && tenantId) {
      generateSetupIntent({
        accountId,
        tenantId,
        token,
        payload: { paymentGatewayId, billGroupId },
      });
    }
  }, [
    generateSetupIntent,
    paymentGatewayId,
    accountId,
    tenantId,
    token,
    billGroupId,
    searchParams,
    setSearchParams,
  ]);

  const onSubmit = async () => {
    // this function would never be able to be called if this data was not already initialized
    if (
      !accountId ||
      !tenantId ||
      !token ||
      !shareData?.paymentSetupIntent.publicKey ||
      !paymentGatewayId ||
      !stripeData?.stripe ||
      !stripeData?.elements ||
      stripeState !== 'READY'
    ) {
      logger.error('Stripe or elements not loaded or not ready');
      return;
    }
    try {
      setIsLoading(true);

      // Trigger stripe form validation
      const { error } = await stripeData.elements.submit();
      if (error) {
        return;
      }

      const paymentMethod = await savePaymentMethod({
        accountId,
        tenantId,
        token,
        payload: {
          stripe: stripeData.stripe,
          elements: stripeData.elements,
          billGroupId,
          paymentGatewayId,
          stripePublicKey: shareData.paymentSetupIntent.publicKey,
        },
      });
      setSavedPaymentMethod(paymentMethod);
    } catch (ex) {
      setSaveErrorMessage(
        'There was a problem saving your payment information. Contact us for assistance.',
      );
      Sentry.captureException(ex, {
        tags: { type: 'RECORD_SHARE' },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isLoadingSetupIntent = _isLoadingSetupIntent || !shareData;
  const isSubmitDisabled = stripeState !== 'READY' || isLoading;

  if (!shareData || isLoadingSetupIntent) {
    return <AppLoading />;
  }

  const {
    paymentSetupIntent: stripeKeys,
    accountName,
    companyLogo,
    companyName,
  } = shareData;

  return (
    <MBox bg="tGray.shareBack" minH="100vh">
      {stripeKeys && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <MFlex
            width="100%"
            maxW={PAPER_PAGE_MAX_WIDTH}
            flexDirection="column"
            pb="10"
            px="12"
            alignItems="center"
            justifyContent="center"
            justifySelf="center"
            minH="100vh"
          >
            <MCard minW="450px" maxW="450px" variant="borderless">
              <Card.Body>
                {!!companyLogo && (
                  <MImage
                    mb={6}
                    src={`data:image/png;base64,${companyLogo}`}
                    alt={`${companyName} logo`}
                    maxHeight={100}
                  />
                )}
                {saveErrorMessage && (
                  <MAlert type="warning" title="Error" width="100%" mb={6}>
                    {saveErrorMessage}
                  </MAlert>
                )}
                {savedPaymentMethod ? (
                  <MAlert
                    type="success"
                    title="Success"
                    width="100%"
                    alertProps={{ bgColor: 'unset' }}
                  >
                    Your payment method has been saved successfully.
                  </MAlert>
                ) : (
                  <>
                    <MText
                      mb={6}
                      display="flex"
                      justifyContent="center"
                      fontSize="md"
                      fontWeight={600}
                    >
                      Welcome, {accountName}.
                    </MText>
                    <StripeWrapper
                      apiKey={stripeKeys.publicKey}
                      clientSecret={stripeKeys.clientSecret}
                    >
                      <PaymentMethodSharePaymentMethod
                        companyName={companyName}
                        onInit={setStripeData}
                        onStripeStateChange={setStripeState}
                      />
                    </StripeWrapper>
                    <MButton
                      mt={6}
                      variant="primary"
                      type="submit"
                      disabled={isSubmitDisabled}
                      isLoading={isLoading}
                      w="100%"
                    >
                      Submit
                    </MButton>
                  </>
                )}
              </Card.Body>
            </MCard>
          </MFlex>
        </form>
      )}
    </MBox>
  );
};
